import { BadgePercent, Home, Search, Sparkles } from "lucide-react";
import SearchBar from "@/components/SearchBar";

const IMG = {
  hero: "https://images.pexels.com/photos/10898888/pexels-photo-10898888.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700",
  noCommission: "https://images.pexels.com/photos/32262467/pexels-photo-32262467.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  hostFriendly: "https://images.unsplash.com/photo-1692619223445-63d5d2c1ec18?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  liveSearch: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
};

export default function Landing() {
  return (
    <div className="text-[#222222] bg-[#F7F7F9]">
      {/* HERO — asymmetric split */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pt-10 md:pt-16 pb-20">
        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-10 lg:gap-16 items-center">
          {/* left */}
          <div>
            <div className="fade-up inline-flex items-center gap-2 bg-[#FF385C]/10 text-[#FF385C] px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide border border-[#FF385C]/20 w-fit mb-8" style={{ animationDelay: "0ms" }}>
              <Sparkles className="w-4 h-4" /> Now live · 0% commission
            </div>
            <h1 className="fade-up font-display font-semibold tracking-tighter leading-[1.02] text-5xl sm:text-6xl md:text-7xl mb-6" style={{ animationDelay: "80ms" }}>
              Travel more.<br />
              Pay <span className="text-[#FF385C]">zero</span> commission.
            </h1>
            <p className="fade-up text-lg md:text-xl leading-relaxed text-[#717171] max-w-xl mb-9" style={{ animationDelay: "160ms" }}>
              Ixitrip is the new-generation OTA that removes hotel commissions — so hosts keep more
              and you pay less. Book direct, transparent stays across India today.
            </p>
            <div className="fade-up" style={{ animationDelay: "240ms" }}>
              <SearchBar />
              <p className="text-sm text-[#717171] mt-3 px-2">Search live, commission-free stays — transparent prices, no hidden fees.</p>
            </div>
          </div>

          {/* right — towering image with distinctive radius */}
          <div className="fade-up hidden lg:block" style={{ animationDelay: "200ms" }}>
            <div className="relative h-[78vh] max-h-[760px] w-full overflow-hidden rounded-tl-[120px] rounded-br-[120px] rounded-tr-2xl rounded-bl-2xl">
              <img src={IMG.hero} alt="Goa beach at sunset with coconut palms" className="absolute inset-0 w-full h-full object-cover hero-zoom" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
              <div className="absolute bottom-8 left-8 right-8 flex items-center gap-3 bg-white/90 backdrop-blur-md rounded-2xl px-5 py-4">
                <span className="w-11 h-11 rounded-xl bg-[#FF385C] flex items-center justify-center shrink-0">
                  <BadgePercent className="w-6 h-6 text-white" />
                </span>
                <div>
                  <p className="font-display font-semibold leading-tight">Save up to 30% per night</p>
                  <p className="text-sm text-[#717171]">vs commission-based booking sites</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS — Bento tetris grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pb-24">
        <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#FF385C] mb-3">Why Ixitrip</p>
        <h2 className="font-display font-medium tracking-tight text-3xl sm:text-4xl md:text-5xl leading-tight mb-12 max-w-2xl">
          A fairer deal for travelers and hosts alike.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 md:grid-rows-2 gap-5">
          {/* Item 1 — No commissions (large) */}
          <div data-testid="benefit-card-no-commission" className="group md:col-span-7 md:row-span-2 relative overflow-hidden rounded-[2rem] bg-white border border-[#EBEBEB] hover:border-[#FF385C]/30 hover:shadow-xl hover:shadow-black/5 transition-all duration-300 min-h-[460px] flex flex-col">
            <div className="p-10 md:p-12">
              <div className="flex items-center justify-between">
                <span className="w-12 h-12 rounded-xl bg-[#FF385C]/10 flex items-center justify-center">
                  <BadgePercent className="w-6 h-6 text-[#FF385C]" strokeWidth={2.2} />
                </span>
                <span className="font-display font-bold text-6xl text-[#FF385C]/15 leading-none">0%</span>
              </div>
              <h3 className="font-display font-semibold text-2xl md:text-3xl mt-6 mb-3">No commissions, ever</h3>
              <p className="text-[#717171] leading-relaxed max-w-md">
                Traditional OTAs skim 15–25% off every booking. We charge hosts 0% — those savings
                flow straight to you as lower prices, and to hosts as real revenue.
              </p>
            </div>
            <div className="mt-auto relative h-56 md:h-64">
              <img src={IMG.noCommission} alt="Aerial view of a Goan beach with palm trees" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
          </div>

          {/* Item 2 — Host-friendly */}
          <div data-testid="benefit-card-host-friendly" className="group md:col-span-5 relative overflow-hidden rounded-[2rem] border border-[#EBEBEB] hover:border-[#FF385C]/30 hover:shadow-xl hover:shadow-black/5 transition-all duration-300 min-h-[220px]">
            <img src={IMG.hostFriendly} alt="Indian villa with a swing and valley view" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-black/45" />
            <div className="relative p-10 h-full flex flex-col justify-between text-white">
              <span className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                <Home className="w-6 h-6 text-white" strokeWidth={2.2} />
              </span>
              <div>
                <h3 className="font-display font-semibold text-2xl mb-2">Host-friendly stays</h3>
                <p className="text-white/85 leading-relaxed">Book directly with hosts who earn more — expect warmer welcomes and better service.</p>
              </div>
            </div>
          </div>

          {/* Item 3 — Live property search */}
          <div data-testid="benefit-card-live-search" className="group md:col-span-5 relative overflow-hidden rounded-[2rem] bg-white border border-[#EBEBEB] hover:border-[#FF385C]/30 hover:shadow-xl hover:shadow-black/5 transition-all duration-300 min-h-[220px]">
            <div className="p-10 h-full flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <span className="w-12 h-12 rounded-xl bg-[#008489]/10 flex items-center justify-center">
                  <Search className="w-6 h-6 text-[#008489]" strokeWidth={2.2} />
                </span>
                <img src={IMG.liveSearch} alt="Taj Mahal, India" className="w-24 h-24 rounded-2xl object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-2xl mb-2">Live property search</h3>
                <p className="text-[#717171] leading-relaxed">Real-time availability and transparent pricing — no hidden fees, no surprises at checkout.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="py-8 border-t border-[#EBEBEB] flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-[#717171]">
          <p>© {new Date().getFullYear()} Ixitrip · Built for a fairer travel ecosystem.</p>
          <p className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF385C]" /> Live across India · Book commission-free</p>
        </div>
      </section>
    </div>
  );
}
